import React, { useMemo } from "react";
import { compose, withHandlers } from "recompose";
import { Trans } from "react-i18next";
import { Col, Button } from "reactstrap";
import { Control, actions, Fieldset, track } from "react-redux-form";
import shortid from "shortid";
import { set, some } from "lodash";
import deepPick from "deepPick";
import { withOrganisationContextConsumer } from "contexts/OrganisationContext";
import { NotificationManager } from "react-notifications";
import {
  Page,
  FormGroup,
  SubmitButton,
  required,
  withLocalForm,
  onMount,
} from "components/common";
import { withXeroErrors } from "components";
import { formatNumber } from "services/util";
import { calculateTaxRate, isCurrency } from "../../../../services/util";
import BackButton from "components/refactoring/BackButton";

export const TaxRateEdit = ({
  LocalForm,
  state,
  taxTypes,
  dispatch,
  XeroErrors,
  onSubmit,
}) => {
  const { TaxComponents } = state;
  const { DisplayTaxRate, EffectiveRate } = useMemo(
    () => calculateTaxRate(TaxComponents),
    [TaxComponents]
  );
  const hasCompound = useMemo(
    () => some(TaxComponents, "IsCompound"),
    [TaxComponents]
  );

  const multipleTaxComponents = TaxComponents.length > 1

  return (
    <Page title="Tax Rates">
      <BackButton/>
      <XeroErrors />
      <LocalForm
        onSubmit={onSubmit}
        validators={{
          Name: { required },
          "TaxComponents[].Name": { required },
          "TaxComponents[].Rate": (v) => {
            return isCurrency(v, { min: 0, decimal_digits: "0,4", max: 100 });
          },
        }}
      >
        <FormGroup label="Tax Rate Display Name">
          <Control.text model=".Name" />
          <div className="form-text text-muted">
            <Trans>
              The name as you would like it to appear in Xero (limited to 50
              characters)
            </Trans>
          </div>
        </FormGroup>
        <FormGroup label="Tax Components" addControlClass={false}>
          <div className="form-control-plaintext">
            {state.TaxComponents.map(({ key, ...i }, idx) => (
              <Control.custom
                model={track(".TaxComponents[]", { key })}
                key={key}
                render={({ fieldValue: { model } }) => (
                  <Fieldset model={model} component="div" className="row mb-2">
                    <Col xs="auto">
                      <FormGroup model=".Name" className="mb-1">
                        <Control.text
                          model=".Name"
                          className="form-control-sm"
                          style={{ width: "20rem" }}
                        />
                      </FormGroup>
                    {multipleTaxComponents && 
                      <FormGroup
                        label="Compound (apply to taxed subtotal)"
                        check
                      >
                        <Control.checkbox
                          model=".IsCompound"
                          changeAction={(m, v) => {
                            dispatch(
                              actions.map("local.TaxComponents", (j, idx2) => {
                                return {
                                  ...j,
                                  IsCompound: idx === idx2 ? v : false,
                                };
                              })
                            );
                          }}
                        />
                      </FormGroup>
                    }
                    </Col>
                    <Col xs="auto">
                      <FormGroup model=".Rate">
                        <div className="input-group input-group-sm">
                          <Control.input
                            type="number"
                            step="0.0001"
                            model=".Rate"
                            className="form-control form-control-sm"
                          />
                          <div className="input-group-append">
                            <div className="input-group-text">%</div>
                          </div>
                        </div>
                      </FormGroup>
                    </Col>
                    <Col xs="auto">
                      {multipleTaxComponents ? (
                        <Button
                          onClick={() =>
                            dispatch(actions.remove("local.TaxComponents", idx))
                          }
                          size="sm"
                          color="link"
                        >
                          <i className="fa fa-trash text-danger" />
                        </Button>
                      ) : null}
                    </Col>
                  </Fieldset>
                )}
              />
            ))}
            {
            <div className="d-flex justify-content-between mt-3">
              <div>
                <Button
                  color="primary"
                  size="sm"
                  className="ml-0"
                  onClick={() =>
                    dispatch(
                      actions.push("local.TaxComponents", {
                        key: shortid(),
                      })
                    )
                  }
                >
                  <i className="fa fa-plus"/> <Trans>Add {multipleTaxComponents && "another " }Tax Component</Trans>
                </Button>
              </div>
              <div>
                <div className="d-flex inv-table">
                  <table>
                    <tbody>
                      <tr>
                        <td>
                          <strong>
                            <Trans>Total tax rate</Trans>
                          </strong>
                        </td>
                        <td className="text-end">
                          <strong>{formatNumber(DisplayTaxRate)} %</strong>
                        </td>
                      </tr>
                      {hasCompound ? (
                        <tr>
                          <td className="mr-4">
                            <Trans>Effective tax rate</Trans>
                          </td>
                          <td className="text-end">
                            {formatNumber(EffectiveRate)} %
                          </td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>

                  <strong></strong>
                </div>
              </div>
            </div>
            }
          </div>
        </FormGroup>
        <div className="d-flex justify-content-end">
          <SubmitButton
            onClick={() => dispatch(actions.submit("local", onSubmit(state)))}
            color="primary"
          >
            Save
          </SubmitButton>
        </div>
      </LocalForm>
    </Page>
  );
};

export const enhance = compose(
  withOrganisationContextConsumer,
  withXeroErrors(),
  onMount(
    ({ xero, match }) =>
      () =>
        Promise.props({
          state:
            match.params.id === "new"
              ? {
                  TaxComponents: [
                    {
                      ReportTaxType: "OUTPUT",
                      key: shortid(),
                    },
                  ],
                }
              : xero
                  .get("taxrates", {
                    params: {
                      TaxType: match.params.id,
                      raw: `TaxType="${match.params.id}"`,
                    },
                  })
                  .get("data")
                  .get(0)
                  .then((d) =>
                    set(
                      d,
                      "TaxComponents",
                      d.TaxComponents.map((i) => set(i, "key", shortid()))
                    )
                  ),
        })
  ),
  withLocalForm({
    initialState: (props) => props.state,
  }),
  withHandlers({
    onSubmit:
      ({ xero, history, setXeroErrors, t, clearContext }) =>
      (tmpItem) => {
        const item = deepPick(tmpItem, [
          "Name",
          "TaxType",
          "ReportTaxType",
          "TaxComponents.Name",
          "TaxComponents.Rate",
          "TaxComponents.IsCompound",
        ]);
        return xero.put("taxrates", item).then(() => {
          NotificationManager.success(t("Saved successfully"));
          clearContext("taxRates");
          history.pushUp();
        }, setXeroErrors);
      },
  })
);

export default enhance(TaxRateEdit);
